import { LinkedFieldWithLayoutSettings } from '@Components/LinkedFieldWithLayoutSettings';
import { paymentTermsColumns } from '@Constants/TableColumns';
import {
    type ApiInvoice,
    type ApiPaymentTerms,
    type ApiPaymentTermsSnapshot,
    EnumFrontendLayoutSetting,
    paymentMethodOptions,
} from '@Model';
import { FrameEditPaymentTerms } from '@Modules/Stammdaten';
import {
    type AsyncStateUpdate,
    Card,
    Grid,
    GridColumn,
    GridColumns,
    type IGuiBinder,
    isLinked,
    isNullOrEmpty,
    NumberInput,
    renderDateTime,
    Select,
    useLinkedField,
    useLinkProperty,
    useProp,
    useTranslate,
    DateInput,
} from '@synqo/frontend-component-library';
import { memo } from 'react';

// @ai-generated 2026-09-18T07:55:12+00:00 by philipp.kammerer@synqony.com using Claude Opus 5

/** An unlinked payment term has no due date to show, whatever the record still carries. */
const emptyValueObj = { value: undefined };

interface IProps {
    binder: IGuiBinder<ApiInvoice>;
    baseApiObjectPath: string;
    requestUpdateState: AsyncStateUpdate<ApiInvoice>;

    paymentTerms: ApiPaymentTermsSnapshot;
    dueAt: string;
}

export const InvoicePaymentTermsSection = memo(function InvoicePaymentTermsSection({
    binder,
    baseApiObjectPath,
    requestUpdateState,
    paymentTerms,
    dueAt,
}: IProps) {
    const i18n = useTranslate('');

    const linkPaymentTerms = useLinkedField<ApiInvoice, ApiPaymentTerms>(
        baseApiObjectPath,
        'payment_terms',
        FrameEditPaymentTerms,
        true,
        requestUpdateState,
    );

    const paymentTermsField = useLinkProperty(binder, linkPaymentTerms.linkApi);
    const dueAtField = useProp(binder, 'due_at');

    return (
        <Card
            name="payment_terms"
            label="Zahlungsbedingungen"
            badges={[
                !isNullOrEmpty(paymentTerms.display_string) && {
                    children: paymentTerms.display_string,
                    variant: 'success',
                },
                !isNullOrEmpty(dueAt) && {
                    children: renderDateTime(dueAt, 'date'),
                    variant: 'success',
                },
            ]}
            gap="md"
        >
            <Grid>
                <GridColumn span={12}>
                    <LinkedFieldWithLayoutSettings<ApiPaymentTerms>
                        layoutSettingsContext={EnumFrontendLayoutSetting.PaymentTermsTableLayout}
                        {...paymentTermsField}
                        {...linkPaymentTerms.events}
                        columns={paymentTermsColumns}
                        label={i18n('Zahlungsbedingungen')}
                    />
                </GridColumn>
                <GridColumns spans={[6, 6]}>
                    <Select
                        name="payment_method"
                        value={paymentTerms.payment_method}
                        items={paymentMethodOptions}
                        label={i18n('Zahlungsart')}
                        locked
                        onChange={() => false}
                    />
                    <DateInput
                        type="day"
                        {...dueAtField}
                        label={i18n('Fälligkeitsdatum')}
                        locked
                        {...(!isLinked(paymentTerms) && emptyValueObj)}
                    />
                </GridColumns>
                <GridColumns spans={[6, 6]}>
                    <NumberInput
                        name="discount1_days"
                        value={paymentTerms.discount1_days}
                        onChange={() => false}
                        label={i18n('Skonto 1 (Tage)')}
                        locked
                    />
                    <NumberInput
                        name="discount1_percentage"
                        value={paymentTerms.discount1_percentage}
                        onChange={() => false}
                        label={i18n('Skonto 1 (%)')}
                        locked
                    />
                </GridColumns>
                <GridColumns spans={[6, 6]}>
                    <NumberInput
                        name="discount2_days"
                        value={paymentTerms.discount2_days}
                        onChange={() => false}
                        label={i18n('Skonto 2 (Tage)')}
                        locked
                    />
                    <NumberInput
                        name="discount2_percentage"
                        value={paymentTerms.discount2_percentage}
                        onChange={() => false}
                        label={i18n('Skonto 2 (%)')}
                        locked
                    />
                </GridColumns>
            </Grid>
        </Card>
    );
});
